import { useEffect, useMemo, useState } from 'react'

import { DocumentSnapshot, limit, orderBy, startAfter, where } from 'firebase/firestore'
import { isEqual, keyBy, sortBy } from 'lodash-es'

import fireChat from 'fireChat'
import useEvent from 'Hooks/useEvent'
import useMounted from 'Hooks/useMounted'
import { useChatAuth } from 'Pages/Chat/AuthProvider'
import { Message } from 'Types'

interface Result {
  hasMore: boolean
  loading: boolean
  messages: Message[]
}

const PAGE_SIZE = 10

const useMessages = (chatId: string, { open }: { open: boolean }): [Result, getMessages: () => Promise<void>] => {
  const { uid } = useChatAuth()
  const [messageMap, setMessageMap] = useState<Record<string, Message>>({})
  const messages = useMemo(
    () => sortBy(Object.values(messageMap), (message) => message.timestamp.toMillis()),
    [messageMap],
  )
  const [loading, setLoading] = useState(false)
  const isMounted = useMounted()
  const [endMessageCursor, setEndMessageCursor] = useState<DocumentSnapshot>()
  const hasMore = !!endMessageCursor

  const markAsRead = useEvent(() => void fireChat.markAsRead(chatId))

  const getMessages = useEvent(async () => {
    if (loading) return
    setLoading(true)

    const queryConstraints = [
      where('action', 'in', ['message']),
      orderBy('timestamp', 'desc'),
      ...(endMessageCursor ? [startAfter(endMessageCursor)] : []),
      limit(PAGE_SIZE),
    ]

    try {
      const [newMessages, { endCursor }] = await fireChat.getMessages(chatId, ...queryConstraints)
      if (!isMounted()) return

      setEndMessageCursor(newMessages.length < PAGE_SIZE ? undefined : endCursor)
      setMessageMap((messageMap) => ({ ...messageMap, ...keyBy(newMessages, 'id') }))
    } finally {
      setLoading(false)
    }
  })

  const subscribeOnMessages = useEvent(() => {
    const queryConstraints = [where('action', 'in', ['message']), orderBy('timestamp', 'desc'), limit(1)]

    return fireChat.subscribeOnMessages(chatId, ...queryConstraints, (newMessages) => {
      if (!isMounted()) return

      let shouldMarkAsRead = false

      setMessageMap((messageMap) => {
        let newMessageMap = messageMap
        newMessages.forEach((message) => {
          if (!isEqual(newMessageMap[message.id], message)) {
            if (!newMessageMap[message.id]) shouldMarkAsRead = true

            newMessageMap = { ...messageMap }
            newMessageMap[message.id] = message
          }
        })
        return newMessageMap
      })

      if (shouldMarkAsRead && open) markAsRead()
    })
  })

  useEffect(() => {
    if (!uid) return

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMessageMap({})
    setEndMessageCursor(undefined)

    let unsubscribe: undefined | (() => void)
    void getMessages().then(() => {
      if (!isMounted()) return

      markAsRead()
      unsubscribe = subscribeOnMessages()
    })

    return () => unsubscribe?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, uid])

  return [{ messages, loading: loading || !uid, hasMore }, getMessages]
}

export default useMessages
